import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { render, scene } from '@/threejs/core';
import { InfiniteGrid } from '@/threejs/core/InfiniteGrid';
import { Color } from 'three';

/**
 * GridManager - Centralized management for InfiniteGrid
 * Handles creation, configuration, and manipulation of the grid in the scene
 */
class GridManager {
  constructor() {
    this.grid = null;
    this.isInitialized = false;
    this._cruxModuleStore = null;
  }

  /**
   * Lazy initialization of store to avoid calling before Pinia is ready
   */
  getCruxModuleStore() {
    if (!this._cruxModuleStore) {
      this._cruxModuleStore = useCruxModuleStore();
    }
    return this._cruxModuleStore;
  }

  /**
   * Initialize the grid and add it to the scene
   * @param {number} divisions - Primary grid divisions (default: 10)
   * @param {number} subdivisions - Grid subdivisions (default: 10)
   * @returns {InfiniteGrid} The created grid instance
   */
  initializeGrid(divisions = 10, subdivisions = 10) {
    if (this.grid) {
      this.removeGrid();
    }

    this.grid = new InfiniteGrid(divisions, subdivisions);
    this.grid.visible = false;
    this.grid.userData.projectorOverlayIgnore = true;

    // Set userData for identification
    this.grid.userData = {
      type: 'infiniteGrid',
      code: 'infiniteGrid',
    };

    scene.add(this.grid);
    this.isInitialized = true;

    return this.grid;
  }

  /**
   * Remove the grid from the scene
   */
  removeGrid() {
    if (this.grid) {
      scene.remove(this.grid);
      this.grid = null;
      this.isInitialized = false;
    }
  }

  /**
   * Get the current grid instance
   * @returns {InfiniteGrid|null}
   */
  getGrid() {
    return this.grid;
  }

  /**
   * Check if grid is initialized
   * @returns {boolean}
   */
  isGridInitialized() {
    return this.isInitialized && this.grid !== null;
  }

  /**
   * Set grid visibility
   * @param {boolean} visible - Whether the grid should be visible
   */
  setGridVisible(visible) {
    if (this.grid) {
      if (this.grid.parent === null) {
        scene.add(this.grid);
      }

      // Check viewport settings - don't show grid in photometric mode
      const cruxModuleStore = this.getCruxModuleStore();
      const viewMode = cruxModuleStore.getSettingValue('viewport_settings', 'render.view');

      // If in photometric mode, always keep grid hidden regardless of visible parameter
      if (viewMode === 'photometric') {
        this.grid.visible = false;
      } else {
        this.grid.visible = visible;
      }

      this.grid.needsUpdate = true;
      render();
    }
  }

  /**
   * Set grid size (primary divisions)
   * Note: This maps to setDivisions in the InfiniteGrid class
   * @param {number} size - Grid size/divisions
   */
  setGridSize(size) {
    if (this.grid && typeof this.grid.setDivisions === 'function') {
      this.grid.setDivisions(size);
      render();
    }
  }

  /**
   * Set grid subdivisions
   * @param {number} subdivisions - Number of subdivisions
   */
  setGridSubdivisions(subdivisions) {
    if (this.grid && typeof this.grid.setSubdivisions === 'function') {
      this.grid.setSubdivisions(subdivisions);
      render();
    }
  }

  /**
   * Set primary grid color
   * @param {THREE.Color|number|string} color - Color for primary grid lines
   */
  setGridColor1(color) {
    if (this.grid && typeof this.grid.setColor1 === 'function') {
      color = color.startsWith('#') ? color : `#${color}`;
      this.grid.setColor1(new Color(color));
      render();
    }
  }

  /**
   * Set secondary grid color
   * @param {THREE.Color|number|string} color - Color for secondary grid lines
   */
  setGridColor2(color) {
    if (this.grid && typeof this.grid.setColor2 === 'function') {
      color = color.startsWith('#') ? color : `#${color}`;
      this.grid.setColor2(new Color(color));
      render();
    }
  }

  /**
   * Set grid fog color
   * @param {THREE.Color|number|string} color - Fog color
   */
  setGridFogColor(color) {
    if (this.grid && typeof this.grid.setFogColor === 'function') {
      this.grid.setFogColor(color);
      render();
    }
  }

  /**
   * Set fog near distance
   * @param {number} near - Near fog distance
   */
  setGridFogNear(near) {
    if (this.grid && typeof this.grid.setFogNear === 'function') {
      this.grid.setFogNear(near);
      render();
    }
  }

  /**
   * Set fog far distance
   * @param {number} far - Far fog distance
   */
  setGridFogFar(far) {
    if (this.grid && typeof this.grid.setFogFar === 'function') {
      this.grid.setFogFar(far);
      render();
    }
  }

  /**
   * Add event listener to grid
   * @param {string} type - Event type
   * @param {function} listener - Event listener function
   */
  addEventListener(type, listener) {
    if (this.grid && typeof this.grid.addGridEventListener === 'function') {
      this.grid.addGridEventListener(type, listener);
    }
  }

  /**
   * Remove event listener from grid
   * @param {string} type - Event type
   * @param {function} listener - Event listener function
   */
  removeEventListener(type, listener) {
    if (this.grid && typeof this.grid.removeGridEventListener === 'function') {
      this.grid.removeGridEventListener(type, listener);
    }
  }

  /**
   * Handle viewport setting changes for grid-related settings
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleGridSetting(key, value) {
    switch (key) {
      case 'grid.enabled':
        this.setGridVisible(value);
        break;
      case 'grid.size':
        this.setGridSize(value);
        break;
      case 'grid.subsections':
      case 'grid.subdivisions':
        this.setGridSubdivisions(value);
        break;
      case 'grid.color1':
        this.setGridColor1(value);
        break;
      case 'grid.color2':
        this.setGridColor2(value);
        break;
      case 'grid.fogColor':
        this.setGridFogColor(value);
        break;
      case 'grid.fogNear':
        this.setGridFogNear(value);
        break;
      case 'grid.fogFar':
        this.setGridFogFar(value);
        break;
      default:
        console.warn(`Unknown grid setting: ${key}`);
    }
  }

  /**
   * Get current grid configuration
   * @returns {object} Current grid settings
   */
  getGridConfig() {
    if (!this.grid) {
      return null;
    }

    return {
      visible: this.grid.visible,
      divisions: this.grid.divisions,
      subdivisions: this.grid.subdivisions,
      // Add other config properties as needed
    };
  }

  /**
   * Dispose of grid resources
   */
  dispose() {
    if (this.grid) {
      // Clean up any resources if needed
      this.removeGrid();
    }
  }
}

// Create and export singleton instance
export const gridManager = new GridManager();

// Export the class for testing or multiple instances if needed
export { GridManager };
